import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Match, MatchWithDetails } from '@/types/database';

export function useMatches(categoryId?: string, liveOnly?: boolean) {
  return useQuery({
    queryKey: ['matches', categoryId, liveOnly],
    queryFn: async () => {
      let query = supabase
        .from('matches')
        .select(`
          *,
          team_a:teams!matches_team_a_id_fkey(*),
          team_b:teams!matches_team_b_id_fkey(*),
          category:categories(*),
          event:events(*)
        `)
        .order('match_time', { ascending: true });
      
      if (categoryId) {
        query = query.eq('category_id', categoryId);
      }
      
      if (liveOnly) {
        query = query.eq('is_live', true);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      return data as unknown as MatchWithDetails[];
    },
  });
}

export function useMatch(id: string) {
  return useQuery({
    queryKey: ['match', id],
    queryFn: async () => {
      const { data: match, error: matchError } = await supabase
        .from('matches')
        .select(`
          *,
          team_a:teams!matches_team_a_id_fkey(*),
          team_b:teams!matches_team_b_id_fkey(*),
          category:categories(*),
          event:events(*)
        `)
        .eq('id', id)
        .single();
      
      if (matchError) throw matchError;
      
      const { data: streams, error: streamsError } = await supabase
        .from('streams')
        .select('*')
        .eq('match_id', id)
        .eq('status', 'live');
      
      if (streamsError) throw streamsError;
      
      return { ...match, streams: streams || [] } as unknown as MatchWithDetails;
    },
    enabled: !!id,
  });
}

export function useCreateMatch() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (match: { 
      team_a_id: string; 
      team_b_id: string; 
      category_id: string; 
      match_time: string; 
      event_id?: string; 
      is_live?: boolean; 
      is_featured?: boolean;
      is_event_match?: boolean;
    }) => {
      const { data, error } = await supabase
        .from('matches')
        .insert(match)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['matches'] });
    },
  });
}

export function useUpdateMatch() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...updates }: { 
      id: string; 
      team_a_id?: string; 
      team_b_id?: string; 
      category_id?: string; 
      match_time?: string; 
      match_end_time?: string | null;
      match_end_time_manual?: boolean;
      event_id?: string; 
      is_live?: boolean; 
      is_featured?: boolean;
      is_event_match?: boolean;
    }) => {
      const { data, error } = await supabase
        .from('matches')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['matches'] });
    },
  });
}

export function useDeleteMatch() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      // Some matches can't be deleted because related tables reference streams/matches.
      // Cleanup dependent rows first, then delete streams, then delete match.

      const { data: streamRows, error: streamFetchError } = await supabase
        .from('streams')
        .select('id')
        .eq('match_id', id);

      if (streamFetchError) throw streamFetchError;

      const streamIds = (streamRows || []).map((s) => s.id);

      // Delete match-linked rows first (prevents FK failures)
      // Execute queries and wrap them properly as promises
      await supabase.from('dmca_requests').delete().eq('match_id', id);
      await supabase.from('click_logs').delete().eq('match_id', id);

      if (streamIds.length > 0) {
        await supabase.from('dmca_requests').delete().in('stream_id', streamIds);
        await supabase.from('click_logs').delete().in('stream_id', streamIds);
        await supabase.from('session_stream_entries').delete().in('stream_id', streamIds);
        await supabase.from('streamer_view_logs').delete().in('stream_id', streamIds);
        await supabase.from('streamer_view_stream_limits').delete().in('stream_id', streamIds);
        await supabase.from('user_activity_logs').delete().in('stream_id', streamIds);
      }

      // Delete related streams to avoid FK constraint errors on matches
      const { error: streamsError } = await supabase.from('streams').delete().eq('match_id', id);
      if (streamsError) throw streamsError;

      // Then delete the match
      const { error } = await supabase.from('matches').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['matches'] });
      queryClient.invalidateQueries({ queryKey: ['streams'] });
    },
  });
}
